// src/components/CustomizeTableColumns.jsx
import { useState, useEffect } from "react";
import "../../styles/customizeTableColumns.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const TABLE_OPTIONS = ["Study List", "Report Summary"];

export default function CustomizeTableColumns({ isOpen, onClose, onSubmit, tableType = "Study List" }) {
  // FIX 1: Initial state "Select Table" rakho — blank start
  const [selectedTable, setSelectedTable] = useState("Select Table");
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [draggedColumn, setDraggedColumn] = useState(null);

  // FIX 2: Sirf tab fetch karo jab valid table select ho
  useEffect(() => {
    if (isOpen && selectedTable !== "Select Table") {
      fetchColumnConfig();
    }
    // Jab modal band ho ya "Select Table" ho, columns clear karo
    if (!isOpen || selectedTable === "Select Table") {
      setColumns([]);
    }
  }, [isOpen, selectedTable]);

  // Modal band hone par state reset karo
  useEffect(() => {
    if (!isOpen) {
      setSelectedTable("Select Table");
      setColumns([]);
      setError("");
    }
  }, [isOpen]);

  const fetchColumnConfig = async () => {
    try {
      setLoading(true);
      setError("");
      
      const user = JSON.parse(localStorage.getItem("user"));
      const centerId = user?.center_id;
      
      const tableTypeValue = selectedTable === "Study List" ? "study_list" : "report_summary";
      
      const response = await fetch(
        `${BASE_URL}/api/column-config/?center_id=${centerId}&table_type=${tableTypeValue}`
      );
      
      if (!response.ok) throw new Error("Failed to load column configuration");
      
      const data = await response.json();
      
      if (data.column_config && data.column_config.columns) {
        setColumns(data.column_config.columns.sort((a, b) => a.order - b.order));
      } else {
        setDefaultColumns();
      }
    } catch (err) {
      console.error("Error fetching column config:", err);
      setError(err.message);
      setDefaultColumns();
    } finally {
      setLoading(false);
    }
  };

  const setDefaultColumns = () => {
    if (selectedTable === "Study List") {
      // FIX 3: Sab visible: false by default
      setColumns([
        { key: 'patient_name', label: 'Patient', visible: false, order: 1 },
        { key: 'patient_id', label: 'Patient Id', visible: false, order: 2 },
        { key: 'study', label: 'Study', visible: false, order: 3 },
        { key: 'referral', label: 'Referral', visible: false, order: 4 },
        { key: 'status', label: 'Status', visible: false, order: 5 },
        { key: 'history', label: 'History', visible: false, order: 6 },
        { key: 'center', label: 'Center', visible: false, order: 7 },
        { key: 'assigned', label: 'Assigned to', visible: false, order: 8 },
        { key: 'studyDate', label: 'Study Date', visible: false, order: 9 },
        { key: 'uploaded', label: 'Uploaded', visible: false, order: 10 },
        { key: 'signedOff', label: 'SignedOff', visible: false, order: 11 },
        { key: 'report', label: 'Report', visible: false, order: 12 },
        { key: 'tag', label: 'Tag', visible: false, order: 13 },
        { key: 'actions', label: 'Actions', visible: false, order: 14 }
      ]);
    } else {
      setColumns([
        { key: 'patient_name', label: 'Patient', visible: false, order: 1 },
        { key: 'study', label: 'Study', visible: false, order: 2 },
        { key: 'studyDate', label: 'Study Date', visible: false, order: 3 },
        { key: 'report', label: 'Report', visible: false, order: 4 },
        { key: 'status', label: 'Status', visible: false, order: 5 }
      ]);
    }
  };

  const toggleColumn = (index) => {
    const updatedColumns = [...columns];
    updatedColumns[index].visible = !updatedColumns[index].visible;
    setColumns(updatedColumns);
  };

  const handleDragStart = (e, index) => {
    setDraggedColumn(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedColumn === null) return;
    
    if (draggedColumn !== index) {
      const updatedColumns = [...columns];
      const draggedItem = updatedColumns[draggedColumn];
      updatedColumns.splice(draggedColumn, 1);
      updatedColumns.splice(index, 0, draggedItem);
      
      updatedColumns.forEach((col, idx) => {
        col.order = idx + 1;
      });
      
      setColumns(updatedColumns);
      setDraggedColumn(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  const handleSubmit = async () => {
    // Agar table select nahi hua to submit mat karo
    if (selectedTable === "Select Table") {
      setError("Please select a table first");
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const user = JSON.parse(localStorage.getItem("user"));
      const centerId = user?.center_id;
      
      const tableTypeValue = selectedTable === "Study List" ? "study_list" : "report_summary";
      
      const response = await fetch(`${BASE_URL}/api/column-config/update/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          center_id: centerId,
          table_type: tableTypeValue,
          column_config: { columns }
        }),
      });
      
      if (!response.ok) throw new Error("Failed to save column configuration");
      
      onSubmit?.({ 
        selectedTable, 
        columnConfig: { columns },
        tableType: tableTypeValue
      });
      onClose?.();
    } catch (err) {
      console.error("Error saving column config:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="ctc-backdrop" onMouseDown={handleBackdrop}>
      <div className="ctc-modal">
        <h2 className="ctc-title">Customize Table Columns</h2>
        <p className="ctc-subtitle">Customize Table Columns Setting</p>
        <p className="ctc-hint">
          Drag &amp; drop to reorder; toggle to show/hide columns
        </p>
        
        {error && <div className="ctc-error">{error}</div>}
        
        <div className="ctc-select-wrapper">
          <label className="ctc-label">Select Table</label>
          <select
            className="ctc-select"
            value={selectedTable}
            onChange={(e) => setSelectedTable(e.target.value)}
          >
            {/* FIX 4: Default placeholder option */}
            <option value="Select Table" disabled>Select Table</option>
            {TABLE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        
        {loading ? (
          <div className="ctc-loading">Loading columns...</div>
        ) : columns.length > 0 ? (
          <div className="ctc-columns-list">
            {columns.map((column, index) => (
              <div
                key={column.key}
                className={`ctc-column-item ${draggedColumn === index ? 'dragging' : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
              >
                <div className="ctc-drag-handle">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="9" cy="12" r="1.5" />
                    <circle cx="15" cy="12" r="1.5" />
                    <circle cx="9" cy="18" r="1.5" />
                    <circle cx="15" cy="18" r="1.5" />
                    <circle cx="9" cy="6" r="1.5" />
                    <circle cx="15" cy="6" r="1.5" />
                  </svg>
                </div>
                <label className="ctc-column-label">
                  <input
                    type="checkbox"
                    checked={column.visible}
                    onChange={() => toggleColumn(index)}
                    className="ctc-checkbox"
                  />
                  {column.label}
                </label>
              </div>
            ))}
          </div>
        ) : null}
        
        <div className="ctc-button-group">
          <button 
            className="ctc-submit-btn" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "SAVING..." : "SUBMIT"}
          </button>
        </div>
        
        <div className="ctc-close-row">
          <button className="ctc-close-btn" onClick={onClose}>
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
}